import React, { useState } from 'react';
import { Button } from './ui/button';
import { getCompanyId, getApiUrl } from '@/lib/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Download, Eye, FileText, Settings, Loader2 } from 'lucide-react';
import { apiService } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

interface JournalPDFGenerationProps {
  entryId?: string;
  entryIds?: string[];
  onClose?: () => void;
  isOpen?: boolean;
}

export function JournalPDFGeneration({
  entryId,
  entryIds = [],
  onClose,
  isOpen = false
}: JournalPDFGenerationProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [options, setOptions] = useState({
    includeAuditTrail: true,
    includeCompanyHeader: true,
    format: 'detailed' as 'detailed' | 'summary'
  });
  const { toast } = useToast();

  const isBatch = entryIds.length > 0;
  const targetIds = isBatch ? entryIds : (entryId ? [entryId] : []);

  const handleGeneratePDF = async () => {
    if (targetIds.length === 0) return;

    setIsGenerating(true);
    try {
      if (isBatch) {
        const blob = await apiService.generateBatchJournalEntryPDF(targetIds, options);
        downloadBlob(blob, `journal-entries-${Date.now()}.pdf`);
        toast({
          title: "PDF Generated",
          description: `Successfully generated PDF for ${targetIds.length} journal entries`
        });
      } else {
        const blob = await apiService.generateJournalEntryPDF(targetIds[0], options);
        downloadBlob(blob, `journal-entry-${targetIds[0]}.pdf`);
        toast({
          title: "PDF Generated",
          description: "Successfully generated PDF for journal entry"
        });
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (targetIds.length === 0 || isBatch) return;

    setIsGenerating(true);
    try {
      const html = await apiService.getJournalEntryPreview(targetIds[0], options);
      setPreviewHtml(html);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Error",
        description: "Failed to generate preview. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handlePrintPreview = () => {
    if (previewHtml) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(previewHtml);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Generate PDF</span>
            </DialogTitle>
            <DialogDescription>
              {isBatch
                ? `Generate PDF for ${entryIds.length} selected journal entries`
                : 'Generate PDF for this journal entry'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* PDF Options */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <Settings className="w-4 h-4" />
                <span>PDF Options</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Format Selection */}
                <div className="space-y-2">
                  <Label htmlFor="format">Format</Label>
                  <Select
                    value={options.format}
                    onValueChange={(value: 'detailed' | 'summary') =>
                      setOptions(prev => ({ ...prev, format: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="detailed">Detailed</SelectItem>
                      <SelectItem value="summary">Summary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Include Company Header */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeCompanyHeader"
                    checked={options.includeCompanyHeader}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, includeCompanyHeader: !!checked }))
                    }
                  />
                  <Label htmlFor="includeCompanyHeader">Include Company Header</Label>
                </div>

                {/* Include Audit Trail */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeAuditTrail"
                    checked={options.includeAuditTrail}
                    onCheckedChange={(checked) =>
                      setOptions(prev => ({ ...prev, includeAuditTrail: !!checked }))
                    }
                  />
                  <Label htmlFor="includeAuditTrail">Include Audit Trail</Label>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {!isBatch && (
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  disabled={isGenerating}
                  className="flex items-center space-x-2"
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  <span>Preview</span>
                </Button>
              )}

              <Button
                onClick={handleGeneratePDF}
                disabled={isGenerating || targetIds.length === 0}
                className="flex items-center space-x-2 flex-1"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>
                  {isGenerating
                    ? 'Generating...'
                    : isBatch
                      ? `Generate PDF (${entryIds.length} entries)`
                      : 'Generate PDF'
                  }
                </span>
              </Button>
            </div>

            {/* Format Descriptions */}
            <div className="text-sm text-muted-foreground space-y-2">
              <div>
                <strong>Detailed Format:</strong> Includes all line details, memos, departments, projects, and locations.
              </div>
              <div>
                <strong>Summary Format:</strong> Shows only essential information for a concise view.
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center space-x-2">
                <Eye className="w-5 h-5" />
                <span>PDF Preview</span>
              </span>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrintPreview}
                  className="flex items-center space-x-1"
                >
                  <FileText className="w-4 h-4" />
                  <span>Print</span>
                </Button>
                <Button
                  size="sm"
                  onClick={handleGeneratePDF}
                  disabled={isGenerating}
                  className="flex items-center space-x-1"
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span>Download PDF</span>
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-auto max-h-[70vh] border rounded-lg">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-full min-h-[600px] border-0"
              title="PDF Preview"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Quick PDF Actions Component for Journal Entry Lists
interface QuickPDFActionsProps {
  entryId: string;
  variant?: 'icon' | 'button';
}

export function QuickPDFActions({ entryId, variant = 'icon' }: QuickPDFActionsProps) {
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const clientSideDownload = async () => {
    const { default: jsPDF } = await import('jspdf')
    const autoTable = (await import('jspdf-autotable')).default as any

    // 1) Try to locate the entry from React Query cache
    let cachedEntry: any = null;
    try {
      const all = queryClient.getQueriesData({ queryKey: ['journal-entries'] }) as any[]
      for (const [, data] of all) {
        const entries = (data as any)?.entries || (data as any)?.data?.entries || []
        const found = Array.isArray(entries) ? entries.find((e: any) => e?.id === entryId) : null
        if (found) { cachedEntry = found; break; }
      }
    } catch { }

    // 2) If not found in cache, fetch by id
    let entry: any = null
    if (!cachedEntry) {
      try { entry = (await apiService.getJournalEntry(entryId)) as any } catch { }
    }
    const src = cachedEntry || (entry as any)?.data || {}

    // Normalize lines and fields
    // Normalize line schema
    const rawLines: any[] = src.lines || src.entryLines || src.journalLines || []
    const headerDate = src.postedAt || src.createdAt || src.date || ''
    const lines: any[] = rawLines.map((l: any) => ({
      date: l.date || l.postingDate || l.createdAt || l.entryDate || headerDate,
      accountCode: l.accountCode || l.account?.code || '',
      accountName: l.accountName || l.account?.name || '',
      memo: l.memo || l.description || '',
      debit: Number(l.debit || l.debitAmount || 0),
      credit: Number(l.credit || l.creditAmount || 0)
    }))
    const ref = src.reference || entryId
    const status = src.status || ''
    const createdAt = src.createdAt || src.created_at || ''
    const postedAt = src.postedAt || src.posted_at || ''

    // Company branding — reuse same source as invoices (through localStorage/config)
    // Get real company by id for accurate name/logo; fallback to localStorage
    let company = { name: 'Your Company', logoUrl: '', primaryColor: '#009688', website: '' }
    try {
      const id = getCompanyId()
      if (id) {
        const c = await apiService.getCompany(id) as any
        if (c) {
          company.name = (c as any).name || company.name
          let logoUrl = (c as any).logoUrl || company.logoUrl

          // Convert relative URL to full URL for PDF generation
          if (logoUrl && logoUrl.startsWith('/api/')) {
            logoUrl = getApiUrl(logoUrl)
          }

          company.logoUrl = logoUrl
          company.website = (c as any).website || company.website
          company.primaryColor = (c as any).primaryColor || company.primaryColor
        }
      }
    } catch (error) {
      company = {
        name: (typeof window !== 'undefined' ? (localStorage.getItem('company_name') || '') : '') || 'Your Company',
        logoUrl: typeof window !== 'undefined' ? (localStorage.getItem('company_logoUrl') || '') : '',
        primaryColor: (typeof window !== 'undefined' ? (localStorage.getItem('company_primaryColor') || '') : '') || '#009688',
        website: typeof window !== 'undefined' ? (localStorage.getItem('company_website') || '') : ''
      }
    }

    const doc = new jsPDF('p', 'mm', 'a4')
    const margin = 20
    let y = margin
    // Professional header with logo and company name on same line
    let logoAdded = false;
    const logoSize = 18; // Larger, more prominent logo
    const logoY = y;

    if (company.logoUrl) {
      try {
        const logoUrl = company.logoUrl.toLowerCase();

        if (logoUrl.includes('.jpg') || logoUrl.includes('.jpeg')) {
          doc.addImage(company.logoUrl, 'JPEG', margin, logoY, logoSize, logoSize)
          logoAdded = true;
        } else if (logoUrl.includes('.png')) {
          doc.addImage(company.logoUrl, 'PNG', margin, logoY, logoSize, logoSize)
          logoAdded = true;
        } else {
          // Try PNG first, fallback to JPEG
          try {
            doc.addImage(company.logoUrl, 'PNG', margin, logoY, logoSize, logoSize)
            logoAdded = true;
          } catch (pngError) {
            try {
              doc.addImage(company.logoUrl, 'JPEG', margin, logoY, logoSize, logoSize)
              logoAdded = true;
            } catch (jpegError) {
              // Continue without logo if both formats fail
            }
          }
        }
      } catch (error) {
        logoAdded = false;
      }
    }

    // Company name aligned with logo center (same line)
    const companyNameY = logoY + (logoSize / 2) + 2; // Center align with logo
    const companyX = logoAdded ? margin + logoSize + 2 : margin; // Reduced gap from 5 to 2
    
    doc.setFontSize(18) // Larger company name font
    doc.setTextColor(0, 0, 0)
    doc.setFont(undefined, 'bold')
    doc.text(company.name, companyX, companyNameY)
    doc.setFont(undefined, 'normal')
    
    // Company website next to name (same line, separated by bullet)
    if (company.website) {
      const nameWidth = doc.getTextWidth(company.name)
      doc.setFontSize(10) // Slightly larger website font
      doc.setTextColor(120, 120, 120)
      doc.text('•', companyX + nameWidth + 2, companyNameY) // Reduced spacing
      doc.text(company.website, companyX + nameWidth + 5, companyNameY) // Reduced from 7 to 5
    }

    // JOURNAL ENTRY title (right side, with background box) - aligned with logo
    doc.setFontSize(15) // Larger title font
    const titleText = 'JOURNAL ENTRY'
    const titleWidth = doc.getTextWidth(titleText) + 12
    const titleHeight = 10
    const titleX = 190 - margin - titleWidth+30; // Increased space from 5mm to 15mm
    const titleY = logoY + (logoSize / 2) - 1; // Align with logo center
    
    // Draw background rectangle for title
    doc.setFillColor(0, 150, 136)
    doc.rect(titleX, titleY - 6, titleWidth, titleHeight, 'F')
    
    // Draw title text on background
    doc.setTextColor(255, 255, 255)
    doc.text(titleText, titleX + 6, titleY)

    // Add separator line below header section
    const separatorY = logoY + logoSize + 5;
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.5)
    doc.line(margin, separatorY, 190 - margin, separatorY)

    doc.setTextColor(0, 0, 0)
    doc.setFontSize(9)

    // Content positioning after separator
    y = separatorY + 8;

    // Document details section - two column layout
    const leftColX = margin;
    const rightColX = 115;

    // Left column details
    doc.text(`Reference: ${ref}`, leftColX, y)
    if (status) {
      doc.text(`Status: ${status}`, leftColX, y + 5)
    }
    if (createdAt) {
      doc.text(`Created: ${new Date(createdAt).toLocaleString()}`, leftColX, y + 10)
    }
    if (postedAt) {
      doc.text(`Posted: ${new Date(postedAt).toLocaleString()}`, leftColX, y + 15)
    }

    // Right column - Generated date
    doc.text(`Generated: ${new Date().toLocaleString()}`, rightColX, y)
    
    y += (postedAt ? 22 : (createdAt ? 17 : (status ? 12 : 7)))

    y += 10
    if (lines.length) {
      // Professional table with better styling
      const totalDebit = lines.reduce((s: number, l: any) => s + Number(l.debit || 0), 0)
      const totalCredit = lines.reduce((s: number, l: any) => s + Number(l.credit || 0), 0)

      autoTable(doc, {
        startY: y,
        head: [['Date', 'Account', 'Memo', 'Debit', 'Credit']],
        body: lines.map((l: any) => {
          const d = l.date ? new Date(l.date) : null
          const dText = d && !isNaN(d.getTime()) ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : ''
          const acct = (l.accountCode ? `${l.accountCode} - ` : '') + (l.accountName || '')
          return [dText, acct, l.memo || '', (l.debit ?? 0).toLocaleString(), (l.credit ?? 0).toLocaleString()]
        }),
        styles: {
          fontSize: 9,
          cellPadding: 4,
          lineColor: [200, 200, 200],
          lineWidth: 0.5
        },
        headStyles: {
          fillColor: [0, 150, 136],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        },
        columnStyles: {
          0: { cellWidth: 25 }, // Date
          1: { cellWidth: 50 }, // Account
          2: { cellWidth: 60 }, // Memo
          3: { halign: 'right', cellWidth: 25 }, // Debit
          4: { halign: 'right', cellWidth: 25 }  // Credit
        },
        margin: { left: margin, right: margin }
      })

      // Professional summary section
      const after = (doc as any).lastAutoTable?.finalY || y + 10

      // Summary box background
      doc.setFillColor(248, 249, 250)
      doc.rect(margin, after + 2, 190 - 2 * margin, 12, 'F')

      // Summary text
      doc.setFontSize(10)
      doc.setTextColor(0, 0, 0)
      doc.text(`Total Debit: ${totalDebit.toLocaleString()}`, margin + 5, after + 8)
      doc.text(`Total Credit: ${totalCredit.toLocaleString()}`, margin + 80, after + 8)

      // Balance status with better styling
      const balanced = Math.abs(totalDebit - totalCredit) < 0.0001
      doc.setTextColor(balanced ? 0 : 200, balanced ? 150 : 0, 0)
      doc.setFontSize(10)
      doc.text(balanced ? '✓ Balanced' : '✗ Unbalanced', 190 - margin - 30, after + 8)
      doc.setTextColor(0, 0, 0)
    } else {
      doc.setFontSize(11)
      const text = 'This is a quick PDF export. Configure the backend PDF route to include lines, totals, and audit trail.'
      const split = (doc as any).splitTextToSize ? (doc as any).splitTextToSize(text, 170) : [text]
      doc.text(split, margin, y)
    }
    // Footer
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    const footer = `${company.website || ''}`
    if (footer) doc.text(footer, margin, 287)
    doc.setTextColor(0, 0, 0)
    doc.save(`journal-entry-${entryId}.pdf`)
  }

  const handleQuickPDF = async () => {
    // Client-first: generate a professional PDF without calling backend (avoids 404 noise)
    await clientSideDownload();
    toast({ title: 'PDF downloaded', description: 'Client-side PDF generated.' });
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (variant === 'button') {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPDFDialog(true)}
          className="flex items-center space-x-1"
        >
          <FileText className="w-4 h-4" />
          <span>PDF</span>
        </Button>

        <JournalPDFGeneration
          entryId={entryId}
          isOpen={showPDFDialog}
          onClose={() => setShowPDFDialog(false)}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleQuickPDF}
        className="h-8 w-8 p-0"
        title="Generate PDF"
      >
        <FileText className="w-4 h-4" />
      </Button>
    </>
  );
}

// Batch PDF Actions Component
interface BatchPDFActionsProps {
  selectedEntries: string[];
  onClearSelection?: () => void;
}

export function BatchPDFActions({ selectedEntries, onClearSelection }: BatchPDFActionsProps) {
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const { toast } = useToast();

  if (selectedEntries.length === 0) return null;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowPDFDialog(true)}
        className="flex items-center space-x-2"
      >
        <FileText className="w-4 h-4" />
        <span>Generate PDF ({selectedEntries.length})</span>
      </Button>

      <JournalPDFGeneration
        entryIds={selectedEntries}
        isOpen={showPDFDialog}
        onClose={() => {
          setShowPDFDialog(false);
          onClearSelection?.();
        }}
      />
    </>
  );
}